import { getResponseOptions } from './getResponseOptions';
import { tracking } from '../strings.locstring.json';
import { isConnectedAccount } from 'owa-accounts-store';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { getCommandBarAddins } from 'owa-calendar-addins';
import { getCalendarEntryByFolderId } from 'owa-calendar-cache';
import { getCalendarItemCategoryMenuFilterableItem } from 'owa-calendar-categories-menu';
import { FullComposeViewState } from 'owa-calendar-compose-types';
import { openSchedulingAssistant } from 'owa-calendar-scheduling-assistant';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import { DisplayOption, FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import { chosenShowAsOptionText, getShowAsMenuItems } from 'owa-freebusy-menu';
import loc, { format } from 'owa-localize';
import { saveButtonLabel } from 'owa-locstrings/lib/strings/savebuttonlabel.locstring.json';
import { sendButtonLabel } from 'owa-locstrings/lib/strings/sendbuttonlabel.locstring.json';
import { isConsumer } from 'owa-session-store';
import {
    discardButtonLabel,
    openSchedulingAssistantTitle,
    showAsCommandBarAriaLabel,
} from './getComposeCommandBarItems.locstring.json';
import {
    canEditCategories,
    hasAttendees,
    isEventAMeeting,
    isEventInstanceOfSeries,
} from 'owa-calendar-event-capabilities';
import {
    onCategoriesChanged,
    onSensitivityToggled,
    presentDiscardClosureConfirmation,
    updateFreeBusy,
    updateSidePaneType,
    verifyEventThenSave,
} from 'owa-calendar-compose-form-actions';
import updateContentToViewState from 'owa-editor/lib/utils/updateContentToViewState';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { customizeActionsText } from 'owa-locstrings/lib/strings/customizeActionsText.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';

export default function getComposeCommandBarItems(
    item: CalendarEvent,
    originalItem: CalendarEvent,
    fullComposeViewState: FullComposeViewState,
    onDismissContextMenu?: () => void,
    calendarColorScheme?: ColorScheme,
    disabled?: boolean
): FilterableMenuItem[] {
    const canSendBehavior = () =>
        isEventAMeeting(item) && isHostAppFeatureEnabled('saveInFullCompose');
    const canSaveBehavior = () =>
        !isEventAMeeting(item) && isHostAppFeatureEnabled('saveInFullCompose');
    const canDiscardBehavior = () => isHostAppFeatureEnabled('discardInFullCompose');
    const shouldShowTrackingBehavior = () =>
        item.MeetingRequestWasSent && hasAttendees(originalItem) && hasAttendees(item);
    const onSaveOrSend = () => {
        updateContentToViewState(fullComposeViewState?.bodyViewState);
        verifyEventThenSave();
    };
    const isEventAnInstanceOfSeries: boolean = isEventInstanceOfSeries(item);

    const smtpOfCalendarOwner =
        item != null && item.ParentFolderId != null
            ? item.ParentFolderId.mailboxInfo.userIdentity
            : originalItem != null && originalItem.ParentFolderId != null
            ? originalItem.ParentFolderId.mailboxInfo.userIdentity
            : undefined;

    const isAConnectedAccount: boolean = isConnectedAccount(smtpOfCalendarOwner);

    // We need to have an additional check for connected account because the WebSessionType is not set to the correct value
    // for connected accounts
    const shouldShowSchedulingAssistant: boolean =
        !isConsumer(smtpOfCalendarOwner) && isHostAppFeatureEnabled('schedulingAssistant');
    const canShowCustomizedActionButton = () =>
        isFeatureEnabled('addin-exp-customizedActionsButtonNavBar');

    let items = [
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(sendButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Send,
            [FilterableMenuItemArgs.onClick]: onSaveOrSend,
            [FilterableMenuItemArgs.shouldShow]: canSendBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(saveButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Save,
            [FilterableMenuItemArgs.onClick]: onSaveOrSend,
            [FilterableMenuItemArgs.shouldShow]: canSaveBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(discardButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Delete,
            [FilterableMenuItemArgs.onClick]: presentDiscardClosureConfirmation,
            [FilterableMenuItemArgs.shouldShow]: canDiscardBehavior,
            [FilterableMenuItemArgs.key]: 'Discard',
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(openSchedulingAssistantTitle),
            [FilterableMenuItemArgs.icon]: ControlIcons.ScheduleEventAction,
            [FilterableMenuItemArgs.onClick]: () => {
                updateSidePaneType(SidePaneType.None); // Close all sidepanes when we open scheduling assistant
                openSchedulingAssistant(false /* isProposeNewTime */);
            },
            [FilterableMenuItemArgs.shouldShow]: () => shouldShowSchedulingAssistant,
            [FilterableMenuItemArgs.disabled]: disabled,
            [FilterableMenuItemArgs.key]: 'SchedulingAssistant',
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(tracking),
            [FilterableMenuItemArgs.icon]: ControlIcons.PeopleAlert,
            [FilterableMenuItemArgs.onClick]: () =>
                fullComposeViewState.currentSidePaneType == SidePaneType.Tracking
                    ? updateSidePaneType(SidePaneType.None)
                    : updateSidePaneType(SidePaneType.Tracking),
            [FilterableMenuItemArgs.shouldShow]: shouldShowTrackingBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: chosenShowAsOptionText(item),
            [FilterableMenuItemArgs.icon]: ControlIcons.ShowTimeAs,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
            [FilterableMenuItemArgs.disabled]: disabled,
            [FilterableMenuItemArgs.menuContent]: {
                items: getShowAsMenuItems(
                    item,
                    updateFreeBusy /* onClick */,
                    calendarColorScheme,
                    onSensitivityToggled,
                    !isEventAnInstanceOfSeries /* showPrivateOption */
                ),
                onDismiss: onDismissContextMenu,
            },
            [FilterableMenuItemArgs.key]: 'ShowAs',
            [FilterableMenuItemArgs.ariaLabel]: format(
                loc(showAsCommandBarAriaLabel),
                chosenShowAsOptionText(item)
            ),
        }),
        getCalendarItemCategoryMenuFilterableItem(
            getCalendarEntryByFolderId(item.ParentFolderId.Id),
            item,
            'CommandBar',
            categoriesChangedHandler,
            categoriesChangedHandler,
            categoriesChangedHandler,
            !isEventAnInstanceOfSeries && canEditCategories(item),
            !isAConnectedAccount && isHostAppFeatureEnabled('settings'), // show manage categories
            true, // show search
            onDismissContextMenu,
            ControlIcons.Tag,
            disabled
        ),
        getResponseOptions(item, disabled, onDismissContextMenu),
        ...(item.ParentFolderId && isHostAppFeatureEnabled('addIns')
            ? getCommandBarAddins(
                  ExtensibilityModeEnum.AppointmentOrganizer,
                  item,
                  item.ParentFolderId.Id,
                  disabled
              )
            : []),
        //Customize action button being added here
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(customizeActionsText),
            [FilterableMenuItemArgs.icon]: ControlIcons.Settings,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
            [FilterableMenuItemArgs.onClick]: () =>
                lazyMountAndShowFullOptions.importAndExecute(
                    'calendar',
                    'calendarSurfaceActions',
                    'calendarSurfaceAddins'
                ),
            [FilterableMenuItemArgs.shouldShow]: canShowCustomizedActionButton,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
    ];

    return items;
}

// this is to avoid rerender when parent component changes - https://flexport.engineering/optimizing-react-rendering-part-1-9634469dca02 - Section ES6 arrow function/bind
const categoriesChangedHandler = newCategoriesList => onCategoriesChanged(newCategoriesList);
